<template>
  <div
    class="offer-selector"
    :class="{
      'offer-selector--eudora': brand === 'eudora',
      'offer-selector--detail-grid': detailGridLayout,
    }"
  >
    <div class="offer-selector__body">
    <p class="offer-selector__label offer-selector__label--main">Escolha uma das opções abaixo:</p>

    <div class="offer-list">
      <div
        v-for="(offer, index) in mainOffers"
        :key="index"
        class="offer-row"
        :class="{
          'offer-row--selected': isMainOfferSelected(offer),
          'offer-row--no-pointer': loadingDueDate,
        }"
        @click="onSelectMain(offer)"
      >
        <div class="offer-row__content">
          <span
            class="offer-row__text"
            :class="{ 'offer-row__text--bold': parseInt(offer.installmentNumber) <= 1 }"
          >
            {{ offer.name }} {{ formatFloatToReal(parseFloat(offer.installmentValue)) }}
          </span>
          <span v-if="parseInt(offer.installmentNumber) <= 1" class="offer-row__badges">
            <svg class="offer-row__fire" width="14" height="17" viewBox="0 0 12 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.16172 6.41295C4.15428 4.93822 5.09363 3.53429 5.20824 1.7642C5.23484 1.35961 5.20005 0.950976 5.21438 0.546385C5.22052 0.360273 5.09568 0.101335 5.35354 0.0123249C5.54387 -0.0524096 5.66461 0.153932 5.78945 0.271263C7.14834 1.55179 8.44173 2.89099 9.54685 4.39809C10.8321 6.14997 11.8471 8.02727 11.9843 10.2303C12.1725 13.2283 10.6745 16.2384 7.38982 16.8838C3.29884 17.6889 -0.444237 14.1932 0.0428338 10.0785C0.255671 8.28621 0.904417 6.69617 1.96042 5.24976C2.07707 5.08994 2.17326 4.83505 2.39837 4.86944C2.63168 4.90383 2.6194 5.17693 2.68284 5.36506C2.7647 5.6058 2.8527 5.84248 2.94889 6.07714C2.98572 6.16615 3.05121 6.24303 3.16172 6.41295ZM5.92861 8.01513C5.94908 9.21879 5.57252 10.0442 5.04656 10.8109C4.9381 10.9686 4.78666 11.3409 4.5554 10.9808C4.1461 10.3415 4.03559 10.8473 3.86777 11.1204C3.55465 11.6261 3.37251 12.1885 3.31521 12.7711C3.19242 13.999 3.58331 15.0328 4.64954 15.7388C5.64005 16.3942 6.68582 16.4286 7.70089 15.8197C8.75894 15.1845 9.12732 14.1427 9.02704 13.0017C8.84694 10.9403 7.49215 9.54651 5.92861 8.01513Z" :fill="accentColor"/>
            </svg>
            <span v-if="discountPercent(offer)" class="offer-row__discount-pill">
              {{ discountPercent(offer) }}%
            </span>
          </span>
        </div>
        <span class="offer-row__radio">
          <span
            class="radio-circle"
            :class="{ 'radio-circle--selected': isMainOfferSelected(offer) }"
          ></span>
        </span>
      </div>
    </div>

    <template v-if="showMoreInstallmentOptions">
      <p class="offer-selector__label offer-selector__label--more">Mais opções de parcelas:</p>
      <div class="styled-select-wrapper">
        <CustomSelect
          :model-value="dropdownInstallmentValue"
          :options="installmentOptions"
          placeholder="Selecione uma opção"
          :variant="brand === 'eudora' ? 'eudora' : 'boticario'"
          :disabled="loadingDueDate"
          @change="onSelectDropdown"
        />
      </div>
    </template>
    </div>

    <div class="offer-selector__footer">
    <button
      class="btn-confirm"
      :data-toggle="modalTarget ? 'modal' : undefined"
      :data-target="modalTarget"
      :disabled="loading || loadingDueDate || !selectedOffer"
      @click="$emit('prepare-agreement', selectedOffer)"
    >
      AVANÇAR
    </button>

    <button
      type="button"
      class="offer-selector__voltar-pendencias"
      :disabled="loadingDueDate"
      @click="$emit('voltar-pendencias')"
    >
      Voltar para pendências
    </button>

    <div v-if="loading">
      <slot name="loading" />
    </div>
    </div>
  </div>
</template>

<script>
import CustomSelect from './CustomSelect.vue';
import { brandAccentColor } from '@/utils/brandColors';

export default {
  name: 'OfferSelector',
  components: { CustomSelect },
  props: {
    brand: { type: String, default: 'boticario' },
    mainOffers: { type: Array, default: () => [] },
    allOffers: { type: Array, default: () => [] },
    selectedOffer: { type: Object, default: null },
    dueDates: { type: Array, default: () => [] },
    dueDate: { type: String, default: null },
    loading: { type: Boolean, default: false },
    loadingDueDate: { type: Boolean, default: false },
    modalTarget: { type: String, default: null },
    detailGridLayout: { type: Boolean, default: false },
  },
  data: () => ({
    offerSelectionSource: null,
  }),
  computed: {
    accentColor() {
      return brandAccentColor(this.brand);
    },
    installmentOptions() {
      return this.allOffers.map(offer => ({
        value: offer.installmentNumber,
        label: `${offer.installmentNumber}x ${this.formatFloatToReal(parseFloat(offer.installmentValue))}${this.brand === 'boticario' ? ` - Total com desc. ${this.formatFloatToReal(parseFloat(offer.totalValue))}` : ''}`,
      }));
    },
    dropdownInstallmentValue() {
      if (this.offerSelectionSource !== 'dropdown' || !this.selectedOffer) return null;
      return this.selectedOffer.installmentNumber;
    },
    showMoreInstallmentOptions() {
      return (this.allOffers || []).some((o) => Number(o.installmentNumber) > 1);
    },
  },
  watch: {
    selectedOffer: {
      handler(val) {
        if (!val) {
          this.offerSelectionSource = null;
          return;
        }
        if (this.offerSelectionSource === 'main') {
          const stillInMain = this.mainOffers.some(o => Number(o.installmentNumber) === Number(val.installmentNumber));
          if (!stillInMain) this.offerSelectionSource = 'dropdown';
          return;
        }
        if (this.offerSelectionSource === 'dropdown') return;

        const inMain = this.mainOffers.some(o => Number(o.installmentNumber) === Number(val.installmentNumber));
        this.offerSelectionSource = inMain ? 'main' : 'dropdown';
      },
      immediate: true,
    },
    mainOffers: {
      handler() {
        if (!this.selectedOffer || this.offerSelectionSource !== 'main') return;
        const stillInMain = this.mainOffers.some(o => Number(o.installmentNumber) === Number(this.selectedOffer.installmentNumber));
        if (!stillInMain) this.offerSelectionSource = 'dropdown';
      },
      deep: true,
    },
  },
  methods: {
    isMainOfferSelected(offer) {
      return this.offerSelectionSource === 'main'
        && this.selectedOffer
        && Number(this.selectedOffer.installmentNumber) === Number(offer.installmentNumber);
    },
    formatFloatToReal(value) {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    discountPercent(offer) {
      const base = offer.updatedValue || offer.originalValue;
      if (!base || base <= 0) return null;
      const d = ((base - offer.installmentValue) / base) * 100;
      return d > 0 ? parseFloat(d.toFixed(2)) : null;
    },
    onSelectMain(offer) {
      if (this.loadingDueDate) return;
      this.offerSelectionSource = 'main';
      this.$emit('select-offer', offer.installmentNumber);
    },
    onSelectDropdown(installmentNumber) {
      if (this.loadingDueDate) return;
      this.offerSelectionSource = 'dropdown';
      this.$emit('select-offer', installmentNumber);
    },
  },
};
</script>

<style scoped>
.offer-selector {
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.offer-selector__body {
  min-width: 0;
}

.offer-selector__footer {
  width: 100%;
  min-width: 0;
}

.offer-selector__label {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #333438;
  margin-bottom: 12px;
}

.offer-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.offer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.2s;
  gap: 12px;
}
.offer-row--selected {
  border-color: #06a472;
}
.offer-row:hover {
  border-color: #06a472;
}

.offer-row--no-pointer {
  pointer-events: none;
  opacity: 0.65;
}

.offer-row__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  flex-wrap: wrap;
}

.offer-row__text {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #333438;
}
.offer-row__text--bold {
  font-weight: 700;
}

.offer-row__badges {
  display: flex;
  align-items: center;
  gap: 6px;
}

.offer-row__fire {
  flex-shrink: 0;
  vertical-align: middle;
}

.offer-selector__label--more {
  margin-top: 24px;
  margin-bottom: 6px;
}

.offer-row__discount-pill {
  background: #06a472;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
}

.offer-row__radio {
  flex-shrink: 0;
}
.radio-circle {
  display: inline-block;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #d0d0d0;
  position: relative;
  transition: border-color 0.2s;
}
.radio-circle--selected {
  border-color: #06a472;
}
.radio-circle--selected::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #06a472;
}

.styled-select-wrapper {
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin-top: 0;
  margin-bottom: 16px;
}
.styled-select {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  padding: 14px 40px 14px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #333438;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23333438' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 16px center;
  cursor: pointer;
}
.styled-select:focus {
  outline: none;
  border-color: #06a472;
}

.btn-confirm {
  width: 100%;
  background-color: #06a472;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 8px;
  transition: background-color 0.2s;
}
.btn-confirm:hover:not(:disabled) {
  background-color: #05966a;
}
.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.offer-selector--eudora .offer-row--selected {
  border-color: #613a53;
}
.offer-selector--eudora .offer-row:hover {
  border-color: #613a53;
}
.offer-selector--eudora .offer-row__discount-pill {
  background: #613a53;
}
.offer-selector--eudora .radio-circle--selected {
  border-color: #613a53;
}
.offer-selector--eudora .radio-circle--selected::after {
  background: #613a53;
}
.offer-selector--eudora .btn-confirm {
  background-color: #613a53;
}
.offer-selector--eudora .btn-confirm:hover:not(:disabled) {
  background-color: #4d2e42;
}
.offer-selector--eudora .styled-select:focus {
  border-color: #613a53;
}

.offer-selector__voltar-pendencias {
  display: none;
}

@media (min-width: 768px) {
  .offer-selector--detail-grid {
    display: contents;
  }

  .offer-selector--detail-grid .offer-selector__body {
    display: contents;
  }

  .offer-selector--detail-grid.offer-selector {
    margin-top: 0;
  }

  .offer-selector__voltar-pendencias {
    display: block;
    width: 100%;
    margin-top: 12px;
    background: none;
    border: none;
    font-family: 'Inter', sans-serif;
    font-size: 0.9rem;
    font-weight: 500;
    color: #176e53;
    text-decoration: underline;
    cursor: pointer;
    padding: 0;
    text-align: center;
  }

  .offer-selector__voltar-pendencias:hover {
    color: #125c44;
  }

  .offer-selector__voltar-pendencias:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .offer-selector__voltar-pendencias:disabled:hover {
    color: #176e53;
  }

  .offer-selector--eudora .offer-selector__voltar-pendencias {
    color: #613a53;
  }

  .offer-selector--eudora .offer-selector__voltar-pendencias:hover {
    color: #4d2e42;
  }

  .offer-selector--eudora .offer-selector__voltar-pendencias:disabled:hover {
    color: #613a53;
  }
}
</style>
